/* global storeeelItems */

import { navigateMediaItem } from '@/helpers/navigateMediaItem';
import { updateBanners, clearImageTimer, isMediaImageReady } from '@/helpers/mediaUtils';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { createSlide } from '@/modules/createSlide';
import { releaseMediaSrcIn } from '@/helpers/deferredMediaSrc';
import { groupMoveButton } from '@/modules/parts/desktop/groupMoveButton';
import { renderGroupMoveButtons, groupMoveButtonAction } from '@/modules/actions/desktop/groupMoveButtonAction';
import { activeVideoPlay } from "@/modules/actions/videoControls";
import { checkVolumeButtonState } from "@/helpers/volumeControl";
import { ctaButton } from '@/modules/parts/ctaButton';
import { clickCtaAction } from '@/modules/actions/clickCtaAction';
import { setupAutoPlayNextAction } from '@/modules/actions/desktop/setupAutoPlayNextAction';
import { slideClickNavigateAction } from "@/modules/actions/desktop/slideClickNavigateAction";
import { animateCurrentProgressBar, prepareCurrentProgressBar } from "@/modules/actions/headerLineDuration";
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { updateGroupSeenBorder } from "@/helpers/updateGroupSeenBorder";
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetViewParams } from "@/helpers/buildMediaAssetViewParams";
import { isTrackableMediaId } from "@/helpers/isTrackableMediaId";
import { buildMediaAssetIdParam } from "@/helpers/buildMediaAssetIdParam";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { isCampaignLoopPlaybackEnabled } from '@/helpers/campaignSettings';
import { closeModal } from '@/helpers/closeModal';
import { normalizeSquareContentIndex } from '@/helpers/squareContentIndex';
import { maybeShowTargetingQuestionnaireForCurrentContent } from '@/helpers/maybeShowTargetingQuestionnaire';
import { isReelLike } from '@/helpers/isReelLike';
import { injectFreqInsertIntoGroup, countNormalContentsSeen, unmarkUnseenFreqInserts } from '@/helpers/streamsTargeting';

export const desktopArrowIconsAction = () => {
  // イベントバインド
  document.querySelectorAll('.arrow-icon-parent').forEach(arrow => {
    arrow.addEventListener('click', (event) => {
      const isPcSquare = isReelLike(window.storeeelItems?.frame);
      // dispatchEvent 由来の非trusted連打だけを抑止し、通常クリックの連続遷移は維持する
      if (isPcSquare && event?.isTrusted === false) {
        if (window._storeeelPcArrowInputLocked) return;
        lockPcArrowInput(getSwiperInstance());
      }
      handleArrow( arrow.id === 'arrow-icon-right-parent' ? 'next' : 'prev' );
    });
  });
};

const isPcSquareModalAlive = () => Boolean(document.querySelector('.storeeel-modal'));

const clearPcArrowInputLockTimer = () => {
  if (window._storeeelPcArrowInputLockTimer == null) return;
  clearTimeout(window._storeeelPcArrowInputLockTimer);
  window._storeeelPcArrowInputLockTimer = null;
};

const releasePcArrowInputLock = () => {
  clearPcArrowInputLockTimer();
  window._storeeelPcArrowInputLocked = false;
};

const lockPcArrowInput = (swiper) => {
  window._storeeelPcArrowInputLocked = true;
  const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 400;
  clearPcArrowInputLockTimer();
  window._storeeelPcArrowInputLockTimer = window.setTimeout(() => {
    releasePcArrowInputLock();
  }, speed + 120);

  if (!swiper || swiper.__storeeelPcArrowInputLockBound) return;
  swiper.__storeeelPcArrowInputLockBound = true;
  swiper.on('slideChangeTransitionEnd', () => {
    releasePcArrowInputLock();
  });
};

const clearPcSquareArrowFallbackTimer = () => {
  if (window._storeeelPcSquareArrowFallbackTimer == null) return;
  clearTimeout(window._storeeelPcSquareArrowFallbackTimer);
  window._storeeelPcSquareArrowFallbackTimer = null;
};

const resolvePcSquareActiveIndex = (swiper) => {
  if (swiper?.params?.loop && Number.isFinite(swiper.realIndex)) {
    return normalizeSquareContentIndex(swiper.realIndex);
  }
  return Number(window.moviePosition || 0);
};

const releasePcSquareArrowTransitionLock = () => {
  clearPcSquareArrowFallbackTimer();
  window._storeeelPcSquareArrowTransitionInProgress = false;
};

const resetPcSquareArrowQueue = () => {
  releasePcSquareArrowTransitionLock();
  window._storeeelPcSquarePendingArrowQueue = [];
  window._storeeelPcSquareArrowQueueRunning = false;
  window._storeeelPcSquareArrowCurrent = null;
};

const bindPcSquareArrowQueueEvents = (swiper) => {
  if (!swiper || swiper.__storeeelPcSquareArrowQueueBound) return;
  swiper.__storeeelPcSquareArrowQueueBound = true;
  swiper.on('slideChangeTransitionEnd', () => {
    settlePcSquareArrowTransition(swiper);
  });
};

const performSquareArrow = (direction, swiper, loopPlaybackEnabled) => {
  clearImageTimer();

  const allContents = (window.storeeelItems?.contentGroups || []).flatMap(group => group.contents || []);
  if (allContents.length === 0) return;

  const realCount = allContents.length;
  const delta = (direction === 'next' ? 1 : -1);
  const hasExtraSlides = Boolean(window._storeeelPcSquareLoopRepeat);

  // loop（複製スライド）環境では realIndex が実際の中央コンテンツに一致するため優先する
  const currentIndex = (swiper?.params?.loop && Number.isFinite(swiper.realIndex))
    ? swiper.realIndex
    : Number(window.moviePosition || 0);
  const currentContentIndex = normalizeSquareContentIndex(currentIndex);
  if (!loopPlaybackEnabled) {
    const lastIndex = allContents.length - 1;
    const nextIndex = currentIndex + delta;
    if (nextIndex < 0 || nextIndex > lastIndex) {
      // ループなしの境界ではキャンペーンを閉じる
      if ((direction === 'next' && nextIndex > lastIndex) || (direction === 'prev' && nextIndex < 0)) {
        closeModal();
        return;
      }
      window.moviePosition = Math.max(0, Math.min(currentIndex, lastIndex));
      if (typeof swiper?.slideTo === 'function') {
        swiper.slideTo(window.moviePosition);
      }
      return;
    }
    window.moviePosition = nextIndex;
    if (typeof swiper?.slideTo === 'function') {
      swiper.slideTo(nextIndex);
    }
    return nextIndex;
  }

  if (swiper?.params?.loop && hasExtraSlides) {
    // 少数コンテンツ時は loopFix を活かして順方向の移動を維持する
    const nextIndex = normalizeSquareContentIndex(currentContentIndex + delta);
    window.moviePosition = nextIndex;
    if (direction === 'next' && typeof swiper?.slideNext === 'function') {
      swiper.slideNext();
      return nextIndex;
    }
    if (direction === 'prev' && typeof swiper?.slidePrev === 'function') {
      swiper.slidePrev();
      return nextIndex;
    }
  }

  if (swiper?.params?.loop) {
    if (typeof swiper.slideToLoop === 'function') {
      const loopIndex = (currentContentIndex + delta + realCount) % realCount;
      window.moviePosition = loopIndex;
      swiper.slideToLoop(loopIndex);
      return loopIndex;
    }
  }

  // loop ではない場合は、末尾→先頭へ「巻き戻し」移動
  const lastIndex = realCount - 1;
  let nextIndex = currentContentIndex + delta;
  if (nextIndex > lastIndex) nextIndex = 0;
  if (nextIndex < 0) nextIndex = lastIndex;
  window.moviePosition = nextIndex;
  if (typeof swiper?.slideTo === 'function') {
    swiper.slideTo(nextIndex);
  }
  return nextIndex;
};

function settlePcSquareArrowTransition(swiper) {
  const current = window._storeeelPcSquareArrowCurrent;
  const actualIndex = resolvePcSquareActiveIndex(swiper);
  const expectedIndex = current?.expectedIndex;
  const shouldRetry = current
    && Number.isFinite(expectedIndex)
    && actualIndex !== expectedIndex
    && (current.retryCount || 0) < 2;

  releasePcSquareArrowTransitionLock();
  window._storeeelPcSquareArrowCurrent = null;

  if (shouldRetry) {
    if (!Array.isArray(window._storeeelPcSquarePendingArrowQueue)) {
      window._storeeelPcSquarePendingArrowQueue = [];
    }
    window._storeeelPcSquarePendingArrowQueue.unshift({
      direction: current.direction,
      retryCount: (current.retryCount || 0) + 1,
    });
  }

  if (window._storeeelPcSquareArrowQueueRunning) {
    window.requestAnimationFrame(flushPcSquareArrowQueue);
  }
}

const flushPcSquareArrowQueue = () => {
  const swiper = getSwiperInstance();
  if (!swiper) {
    if (!isPcSquareModalAlive()) {
      resetPcSquareArrowQueue();
      return;
    }
    window.requestAnimationFrame(flushPcSquareArrowQueue);
    return;
  }

  bindPcSquareArrowQueueEvents(swiper);

  const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 400;
  const lastIssuedAt = Number(window._storeeelPcSquareArrowLastIssuedAt || 0);
  const isAnimatingStale = Boolean(swiper.animating)
    && lastIssuedAt > 0
    && (Date.now() - lastIssuedAt) > (speed + 800);

  if (isAnimatingStale) {
    try {
      swiper.animating = false;
    } catch {
      // Swiper の内部フラグが残留した場合だけ補正する
    }
  }

  if (window._storeeelPcSquareArrowTransitionInProgress || (swiper.animating && !isAnimatingStale)) {
    window.requestAnimationFrame(flushPcSquareArrowQueue);
    return;
  }

  if (!Array.isArray(window._storeeelPcSquarePendingArrowQueue) || window._storeeelPcSquarePendingArrowQueue.length === 0) {
    window._storeeelPcSquareArrowQueueRunning = false;
    return;
  }

  const nextArrow = window._storeeelPcSquarePendingArrowQueue.shift();
  const nextDirection = typeof nextArrow === 'string' ? nextArrow : nextArrow?.direction;
  if (!nextDirection) {
    window.requestAnimationFrame(flushPcSquareArrowQueue);
    return;
  }

  // slideToLoop/slideNext 呼び出し直後は animating がまだ立っていないことがあるため、
  // 手動ロックで「1遷移につき1件」だけ消化する。
  window._storeeelPcSquareArrowTransitionInProgress = true;
  window._storeeelPcSquareArrowLastIssuedAt = Date.now();
  const expectedIndex = performSquareArrow(nextDirection, swiper, isCampaignLoopPlaybackEnabled(window.storeeelItems));
  window._storeeelPcSquareArrowCurrent = {
    direction: nextDirection,
    expectedIndex,
    retryCount: typeof nextArrow === 'string' ? 0 : Number(nextArrow?.retryCount || 0),
  };
  clearPcSquareArrowFallbackTimer();
  window._storeeelPcSquareArrowFallbackTimer = window.setTimeout(() => {
    settlePcSquareArrowTransition(getSwiperInstance());
  }, speed + 120);
};

const enqueuePcSquareArrow = (direction) => {
  if (!Array.isArray(window._storeeelPcSquarePendingArrowQueue)) {
    window._storeeelPcSquarePendingArrowQueue = [];
  }
  window._storeeelPcSquarePendingArrowQueue.push({ direction, retryCount: 0 });
  if (window._storeeelPcSquareArrowQueueRunning) return;
  window._storeeelPcSquareArrowQueueRunning = true;
  window.requestAnimationFrame(flushPcSquareArrowQueue);
};

// グループ切り替え時の再描画処理
function reRenderGroup(slideIndex, contents) {
  const swiper   = getSwiperInstance();
  const wrapper  = document.querySelector('.storeeel-swiper-group-wrapper .swiper-wrapper');
  const modal    = document.querySelector('.storeeel-modal');

  // 外すだけでは取得が止まらないことがあるため、先に動画を解放する
  releaseMediaSrcIn(wrapper);
  wrapper.innerHTML = '';
  // 表示するスライド以外は src を保留し、中央 → 隣の順に読み込む（pcMediaLoadWindow）
  contents.forEach((content, index) => {
    wrapper.appendChild(createSlide(content, { deferSrc: index !== slideIndex }));
  });
  swiper.update();
  swiper.slideTo(slideIndex);

  const idx = storeeelItems.contentGroups.findIndex(g => g.id === window.iconId);
  const groups = storeeelItems.contentGroups || [];
  const shouldLoopGroup = isCampaignLoopPlaybackEnabled(window.storeeelItems) && groups.length > 1;
  if (shouldLoopGroup && idx !== -1) {
    window.prevGroup = groups[(idx - 1 + groups.length) % groups.length];
    window.nextGroup = groups[(idx + 1) % groups.length];
  } else {
    window.prevGroup = groups[idx - 1];
    window.nextGroup = groups[idx + 1];
  }
  modal.insertAdjacentHTML('beforeend', groupMoveButton());
  renderGroupMoveButtons();
  groupMoveButtonAction();
  slideClickNavigateAction();
  // 進捗バーのアニメーションを開始
  const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
  showMediaLoadingShimmer(activeWrapper);
  const video = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper video.storeeel-movie');
  if (video) {
    if (video._onLoadedMetadataForProgress) {
      video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
      video._onLoadedMetadataForProgress = null;
    }
    if (video._onPlayingForProgress) {
      video.removeEventListener('playing', video._onPlayingForProgress);
      video._onPlayingForProgress = null;
    }

    const cleanup = () => {
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }
    };

    const tryStartProgress = () => {
      if (video.paused || video.ended) return false;
      if (video.readyState < 2) return false;
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return false;
      animateCurrentProgressBar(duration, slideIndex);
      hideMediaLoadingShimmer(activeWrapper);
      cleanup();
      return true;
    };

    // 再生開始まで進捗を開始しない
    prepareCurrentProgressBar(slideIndex);
    video._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
    video._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
    video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
    video.addEventListener('playing', video._onPlayingForProgress);
    tryStartProgress();
  } else {
    const image = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper img.storeeel-media-image');
    if (image) {
      if (image._onLoadForProgress) {
        image.removeEventListener('load', image._onLoadForProgress);
        image._onLoadForProgress = null;
      }
      if (image._onErrorForProgress) {
        image.removeEventListener('error', image._onErrorForProgress);
        image._onErrorForProgress = null;
      }

      const cleanup = () => {
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }
      };

      const tryStartProgress = () => {
        if (!isMediaImageReady(image)) return false;
        prepareCurrentProgressBar(slideIndex);
        animateCurrentProgressBar(5, slideIndex);
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
        return true;
      };

      // 読み込み完了後に進捗を開始する
      image._onLoadForProgress = () => { tryStartProgress(); };
      image._onErrorForProgress = () => {
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
      };
      image.addEventListener('load', image._onLoadForProgress);
      image.addEventListener('error', image._onErrorForProgress);
      tryStartProgress();
    }
  }

  maybeShowTargetingQuestionnaireForCurrentContent();
}

// スライドの動画再生
// 引数に次のスライドか前のスライドかを渡す
export const handleArrow = (direction) => {
  const swiper   = getSwiperInstance();
  const isCircle = window.storeeelItems.frame === "circle";
  const isSquare = isReelLike(window.storeeelItems.frame);

  if (isSquare) {
    enqueuePcSquareArrow(direction);
    return;
  }

  // 画像タイマーのクリア
  clearImageTimer();

  // circle 型: グループ境界を超えるかもしれないため、遷移前のグループ情報を記録する
  const prevGroupIdForFreq = isCircle ? window.iconId : null;
  const prevMoviePositionForFreq = isCircle ? Number(window.moviePosition || 0) : 0;

  // PC + square は「全コンテンツをフラットに並べた swiper」のため、
  // group 単位の navigateMediaItem ではなく swiper index を基準にループさせる。
  let contents;
  // 1. 位置更新
  window.moviePosition += (direction === 'next' ? 1 : -1);
  // 2. コンテンツ取得＆モーダル判定
  contents = navigateMediaItem();
  if (!contents && isCircle) {
    // PC + circle：ループなしで境界矢印を押したらキャンペーンを閉じる
    closeModal();
    return;
  }

  // 3. バナー更新
  updateBanners();

  // 4. グループ切り替え判定
  const isBoundary = isCircle && (direction === 'next' ? window.moviePosition === 0 : window.moviePosition === contents.length - 1);
  if (isBoundary) {
    // circle グループ境界: 頻度挿入状態を更新して新グループに注入する
    const freqState = window._storeeelFreqInsertCircleState;
    if (freqState && prevGroupIdForFreq && String(prevGroupIdForFreq) !== String(window.iconId)) {
      const groups = window.storeeelItems?.contentGroups || [];
      const prevGroupIdx = groups.findIndex((g) => String(g.id) === String(prevGroupIdForFreq));
      const newGroupIdx = groups.findIndex((g) => String(g.id) === String(window.iconId));
      if (direction === 'next' && prevGroupIdx >= 0) {
        freqState.contentsSeen += countNormalContentsSeen(groups[prevGroupIdx], prevMoviePositionForFreq);
        // 未到達位置のfreqを displayedIds から除去し、次グループで再利用できるようにする
        unmarkUnseenFreqInserts(groups[prevGroupIdx], prevMoviePositionForFreq, freqState);
        // 前グループの最後がfreqだった場合、次グループ先頭へのpre-insertをスキップする
        freqState.skipNextPreInsert = groups[prevGroupIdx].contents[prevMoviePositionForFreq]?.isFrequencyInsertContent === true;
      }
      if (newGroupIdx >= 0 && !groups[newGroupIdx]._freqInsertInjected) {
        const injectedGroup = injectFreqInsertIntoGroup(groups[newGroupIdx], freqState);
        injectedGroup._freqInsertInjected = true;
        groups[newGroupIdx] = injectedGroup;
        window.storeeelItems = { ...window.storeeelItems, contentGroups: groups };
        contents = injectedGroup.contents;
        // prev 方向のとき navigateMediaItem が設定した moviePosition は注入前の長さ基準なので更新する
        if (direction === 'prev') {
          window.moviePosition = contents.length - 1;
        }
      }
    }
    // circleかつ境界のときのみグループ再描画
    reRenderGroup(direction === 'next' ? 0 : contents.length - 1, contents);
    // CTAボタンを再描画
    document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide .slide-media-wrapper').forEach((wrapper, index) => {
      const ctaHost = ctaButton(contents[index]);
      if (ctaHost) {
        wrapper.style.cursor = 'pointer';
        wrapper.appendChild(ctaHost);
      }
    });
    clickCtaAction();
  } else if (isCircle) {
    // circle の通常スライド
    swiper[direction === 'next' ? 'slideNext' : 'slidePrev']();
  }

  // 7. スライドの動画再生
  activeVideoPlay();
  // 8. 音量ボタンの状態に応じて動画のミュート変更
  checkVolumeButtonState();
  // 9. 対象の進捗バースタート
  const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
  showMediaLoadingShimmer(activeWrapper);
  const video = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper video.storeeel-movie');
  const progressIndex = isCircle ? window.moviePosition : 0;
  if (video && video.style.display !== 'none') {
    if (video._onLoadedMetadataForProgress) {
      video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
      video._onLoadedMetadataForProgress = null;
    }
    if (video._onPlayingForProgress) {
      video.removeEventListener('playing', video._onPlayingForProgress);
      video._onPlayingForProgress = null;
    }

    const cleanup = () => {
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }
    };

    const tryStartProgress = () => {
      if (video.paused || video.ended) return false;
      if (video.readyState < 2) return false;
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return false;
      animateCurrentProgressBar(duration, progressIndex);
      hideMediaLoadingShimmer(activeWrapper);
      cleanup();
      return true;
    };

    // 再生開始まで進捗を開始しない
    prepareCurrentProgressBar(progressIndex);
    video._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
    video._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
    video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
    video.addEventListener('playing', video._onPlayingForProgress);
    tryStartProgress();
  } else {
    const image = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper img.storeeel-media-image');
    if (image) {
      if (image._onLoadForProgress) {
        image.removeEventListener('load', image._onLoadForProgress);
        image._onLoadForProgress = null;
      }
      if (image._onErrorForProgress) {
        image.removeEventListener('error', image._onErrorForProgress);
        image._onErrorForProgress = null;
      }

      const cleanup = () => {
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }
      };

      const tryStartProgress = () => {
        if (!isMediaImageReady(image)) return false;
        animateCurrentProgressBar(5, progressIndex);
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
        return true;
      };

      // 読み込み完了後に進捗を開始する
      prepareCurrentProgressBar(progressIndex);
      image._onLoadForProgress = () => { tryStartProgress(); };
      image._onErrorForProgress = () => {
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
      };
      image.addEventListener('load', image._onLoadForProgress);
      image.addEventListener('error', image._onErrorForProgress);
      tryStartProgress();
    }
  }
  // 10. 動画終了後の次の動画を再生
  setupAutoPlayNextAction();

  // 11. 計測とローカルストレージ保存
  let groupId;
  if (window.storeeelItems.frame === "circle") {
    groupId = window.iconId;
  } else {
    groupId = null;
  }

  // スライド型の最後のスライドから戻った時のcontentsが取得できないため、調査必要
  if (!contents) return;

  // プロモーション（頻度挿入）コンテンツとそれ以外でイベント名を分ける。
  // プロモーションは campaign_id を送らない（キャンペーンレポートへの混入防止）。
  // media_id が空のイベント/ログは集計に乗らないため送らない（isTrackableMediaId のコメント参照）
  const trackedMediaId = contents[window.moviePosition]?.isFrequencyInsertContent
    ? contents[window.moviePosition].frequencyInsertContentId
    : contents[window.moviePosition]?.mediaAsset?.id;

  if (isTrackableMediaId(trackedMediaId)) {
    if (contents[window.moviePosition]?.isFrequencyInsertContent) {
      trackStoreeelEvent('storeeel_frequency_insert_media_view', {
        storeeel_name: contents[window.moviePosition].mediaAsset.name,
        ...buildMediaAssetIdParam(
          contents[window.moviePosition].frequencyInsertContentId,
          contents[window.moviePosition].mediaAsset.type,
        ),
        campaign_id: window.storeeelItems.id,
        ...(groupId ? { group_id: groupId } : {}),
      });
    } else {
      trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
        storeeelName: contents[window.moviePosition].mediaAsset.name,
        groupId,
        mediaId: contents[window.moviePosition].mediaAsset.id,
        campaignId: window.storeeelItems.id,
        mediaAssetType: contents[window.moviePosition].mediaAsset.type,
      }));
    }
  }
  if (isTrackableMediaId(contents[window.moviePosition]?.mediaAsset?.id)) {
    localStorageRepositories.get("mediaView").set(
      contents[window.moviePosition].mediaAsset.id,
      window.storeeelItems.id,
      groupId,
      contents[window.moviePosition].mediaAsset.type
    );
  }
  // 無期限の既読フラグも保存（スライド型は保存しない）
  try {
    if (window.storeeelItems?.frame !== 'square') {
      localStorageRepositories.get("contentSeen").set(contents[window.moviePosition].id);
      if (groupId) updateGroupSeenBorder(groupId);
    }
  } catch { void 0; }
}
